import * as React from "react";
import { AutocompleteResults } from "../../../containers/AutocompleteResultsContainer";
import { Box } from "@mui/material";
import { EntityPolygon, Hangar, Location, Ramp, Stack } from "../../../types";
import { findTenantsFullyInHangar } from "../../../screens/Hangar/HangarInfoCard";
import { FullyLoadedDraggableAircraft } from "../FullyLoadedDraggableAircraft";
import { useIsInAnyLayout } from "../hooks/useIsInLayout";
import { useMultiHangarState } from "../../../containers/MultiHangarContainer";

type Props = {
  location: Location<Hangar | Ramp>;
  stack: Stack;
  polygons: EntityPolygon[];
  autocompleteResults: AutocompleteResults;
  feetToPixels: number;
  mouseOverIndex: number;
  height: number;
};

export const GhostAircraft: React.FC<Props> = ({
  location,
  stack,
  polygons,
  autocompleteResults,
  feetToPixels,
  mouseOverIndex,
  height,
}) => {
  const { isMultiHangar, hangars } = useMultiHangarState();
  const isInAnyLayout = useIsInAnyLayout({ feetToPixels }, []);

  // Find selected tenant from the stack prop (which is the correct stack for this view)
  const selectedTenant =
    stack.tenants.find((t) => t.selected) ||
    hangars
      .find((h) => h.stack.tenants.find((t) => t.selected))
      ?.stack.tenants.find((t) => t.selected);
  const { tenant: tenantBeingAutocompleted } = autocompleteResults;

  const selected = Boolean(
    stack.tenants.find((t) => t.selected) ||
      hangars.find((h) => h.stack.tenants.find((t) => t.selected))
  );
  if (!selected) {
    return null;
  }

  if (!tenantBeingAutocompleted) {
    return null;
  }

  if (selectedTenant?.id !== tenantBeingAutocompleted.id) {
    return null;
  }

  // Check if tenant is fully in the location (only for hangars)
  if (location.type === "hangar") {
    // Create a hangar-like object with the current stack and location dimensions
    const hangarForCheck = {
      ...location,
      stack,
    } as Hangar;

    if (
      findTenantsFullyInHangar(hangarForCheck, polygons).find(
        (t) => t.id === tenantBeingAutocompleted.id
      )
    ) {
      return null;
    }
  }

  if (isMultiHangar && isInAnyLayout(tenantBeingAutocompleted)) {
    return null;
  }

  return (
    <React.Fragment
      key={`suggested-aircraft-${stack.id}-${tenantBeingAutocompleted?.entity_id}`}
    >
      {autocompleteResults.suggestedPositions
        .filter((x) => x)
        ?.map((suggestedPosition, idx) => {
          return (
            <Box
              key={`suggested-position-aircraft-${stack.id}-${suggestedPosition.id}`}
              sx={{
                opacity: idx === mouseOverIndex ? 0.9 : 0.4,
                filter:
                  idx === mouseOverIndex ? "brightness(0.3)" : "brightness(1)",
              }}
            >
              <FullyLoadedDraggableAircraft
                readOnly={true}
                layoutHeight={height}
                feetToPixels={feetToPixels}
                tenant={{
                  ...tenantBeingAutocompleted,
                  id: suggestedPosition.id,
                  selected: false,
                  position: {
                    ...tenantBeingAutocompleted.position,
                    ...suggestedPosition,
                  },
                }}
                stack={stack}
                setStack={() => {}}
                preferences={[]}
                onPositionChange={() => {}}
                rightClick={null}
                setRightClick={() => {}}
                spacingBufferOverlayElement={null}
              />
            </Box>
          );
        })}
    </React.Fragment>
  );
};
